import MuiLink from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import Image from 'next/image'
import Link from 'next/link'
import { FC, Fragment, ReactNode } from 'react'
import { colors } from '../../../shared/theme/colors'

type Mark = 'strong' | 'emphasis' | 'underline' | 'strikethrough' | 'code' | 'highlight'

type SpanNode = {
  type: 'span'
  value: string
  marks?: Mark[]
}

type LinkNode = {
  type: 'link'
  url: string
  children: SpanNode[]
}

type InlineNode = SpanNode | LinkNode

type ParagraphNode = { type: 'paragraph'; children: InlineNode[] }
type HeadingNode = { type: 'heading'; level: number; children: InlineNode[] }
type ListItemNode = { type: 'listItem'; children: BlockNode[] }
type ListNode = { type: 'list'; style: 'bulleted' | 'numbered'; children: ListItemNode[] }
type BlockquoteNode = { type: 'blockquote'; children: BlockNode[]; attribution?: string }
type ThematicBreakNode = { type: 'thematicBreak' }
type BlockRefNode = { type: 'block'; item: string }

type BlockNode =
  ParagraphNode | HeadingNode | ListNode | BlockquoteNode | ThematicBreakNode | BlockRefNode

type RootNode = { type: 'root'; children: BlockNode[] }

export type StructuredTextDocument = {
  schema: 'dast'
  document: RootNode
}

export type SvgBlock = {
  __typename: 'SvgblockRecord'
  id: string
  svgImage: { url: string; alt: string | null; width: number; height: number } | null
}

export type StructuredTextBlock = SvgBlock

const HEADING_VARIANTS: Record<number, 'h2' | 'h3' | 'h4' | 'h5'> = {
  1: 'h2',
  2: 'h3',
  3: 'h4',
  4: 'h5',
}

const MARK_CLASSNAMES: Record<Mark, string> = {
  strong: 'font-bold',
  emphasis: 'italic',
  underline: 'underline',
  strikethrough: 'line-through',
  code: 'font-mono bg-ink-100 px-1 rounded',
  highlight: 'bg-yellow-300',
}

const renderSpan = (node: SpanNode, key: number): ReactNode => {
  const className = (node.marks ?? []).map((mark) => MARK_CLASSNAMES[mark]).join(' ')
  if (!className) return <Fragment key={key}>{node.value}</Fragment>
  return (
    <span key={key} className={className}>
      {node.value}
    </span>
  )
}

const renderInline = (nodes: InlineNode[]): ReactNode =>
  nodes.map((node, index) => {
    if (node.type === 'link') {
      const isInternal = node.url.startsWith('/')
      const label = node.children.map((child, childIndex) => renderSpan(child, childIndex))
      return isInternal ? (
        <Link key={index} href={node.url} className="text-green-700 hover:underline">
          {label}
        </Link>
      ) : (
        <MuiLink key={index} href={node.url} target="_blank" rel="noopener noreferrer">
          {label}
        </MuiLink>
      )
    }
    return renderSpan(node, index)
  })

const renderEmbeddedBlock = (block: StructuredTextBlock | undefined, key: number): ReactNode => {
  if (block?.__typename === 'SvgblockRecord' && block.svgImage) {
    return (
      <div key={key} className="flex justify-center">
        <Image
          src={block.svgImage.url}
          alt={block.svgImage.alt ?? ''}
          width={block.svgImage.width}
          height={block.svgImage.height}
          unoptimized
          className="h-auto max-w-full"
        />
      </div>
    )
  }
  return null
}

const renderBlock = (
  node: BlockNode,
  key: number,
  blocks: Map<string, StructuredTextBlock>
): ReactNode => {
  switch (node.type) {
    case 'heading':
      return (
        <Typography
          key={key}
          variant={HEADING_VARIANTS[node.level] ?? 'h5'}
          sx={{ color: colors.Ink[900] }}
        >
          {renderInline(node.children)}
        </Typography>
      )
    case 'paragraph':
      return (
        <Typography key={key} variant="p1" sx={{ color: colors.Ink[700] }}>
          {renderInline(node.children)}
        </Typography>
      )
    case 'list': {
      const ListTag = node.style === 'numbered' ? 'ol' : 'ul'
      const listClass = node.style === 'numbered' ? 'list-decimal' : 'list-disc'
      return (
        <ListTag key={key} className={`${listClass} flex flex-col gap-2 pl-8`}>
          {node.children.map((item, itemIndex) => (
            <li key={itemIndex}>
              {item.children.map((child, childIndex) => renderBlock(child, childIndex, blocks))}
            </li>
          ))}
        </ListTag>
      )
    }
    case 'blockquote':
      return (
        <blockquote key={key} className="border-l-4 border-green-700 pl-6">
          {node.children.map((child, childIndex) => renderBlock(child, childIndex, blocks))}
          {node.attribution && (
            <Typography variant="p2" sx={{ color: colors.Ink[600] }}>
              {node.attribution}
            </Typography>
          )}
        </blockquote>
      )
    case 'thematicBreak':
      return <hr key={key} className="border-ink-300" />
    case 'block':
      return renderEmbeddedBlock(blocks.get(node.item), key)
    default:
      return null
  }
}

interface StructuredTextProps {
  data: StructuredTextDocument
  blocks?: StructuredTextBlock[]
}

const StructuredText: FC<StructuredTextProps> = ({ data, blocks = [] }) => {
  const blocksById = new Map(blocks.map((block) => [block.id, block]))

  return (
    <div className="flex flex-col gap-6">
      {data.document.children.map((node, index) => renderBlock(node, index, blocksById))}
    </div>
  )
}

export default StructuredText
